import "./EventListTable.scss";
import ChannelSelection from "@/components/Events/ChannelSelection/ChannelSelection.vue";
import EventTableActionMenu from "@/components/Events/EventTableActionMenu/EventTableActionMenu.vue";
import {
  getPrincipalName,
  getApiKeyDescription,
  getThumbnailUrl,
  getObjectDiff
} from "@/helpers/commonHelper";
import { imports } from "@/helpers/eventHelper";

export default {
  name: "EventListTable",
  components: { ChannelSelection, EventTableActionMenu },
  data: () => ({
    page: 1,
    pageCount: 0,
    itemsPerPage: 10
  }),
  props: [
    "showPreviewImage",
    "config",
    "headers",
    "events",
    "copyEvent",
    "editEvent",
    "previewEvent",
    "deleteEvent",
    "eventCount",
    "getTypeIcon",
    "getStartTime",
    "getOrganizer",
    "getLocation",
    "getLocationCity",
    "filter",
    "loadingExport",
    "exportType",
    "isLoading",
    "getRegionality"
  ],
  methods: {
    nextPage() {
      const totalPages = Math.ceil(this.eventCount / this.itemsPerPage);
      if (this.page < totalPages) this.page++;
    },
    previousPage() {
      if (this.page > 1) this.page--;
    },
    triggerFilter() {
      const { page } = this.tableOptions;
      this.$emit("setFilterPage", page);
    },
    getPrincipalName,
    getApiKeyDescription,
    getThumbnailUrl,
    previewImage(item) {
      const url = item?.pictures[0]?.url;
      return this.getThumbnailUrl(url, "events", 70, "-");
    },
    morePictures(item) {
      return item.pictures.length > 1;
    },
    getTitle(item) {
      if (item.moment?.title?.length) {
        return item.moment.title;
      }
      return item.title;
    },
    updateItemsPerPage(value) {
      this.$emit("setFilterLimit", value);
    },
    editHelper(item) {
      this.$emit("editEvent", item);
    },
    deleteHelper(item) {
      if (!item.deleted) this.$emit("deleteEvent", item);
    },
    copyHelper(item) {
      if (!item.deleted) this.$emit("copyEvent", item);
    },
    checkBoxHighlightHelper(item) {
      if (!item.deleted) {
        item.highlight[this.config.useHighlightFrom] = !item.highlight[
          this.config.useHighlightFrom
        ];
        this.$emit("updateCheckboxState", {
          item,
          prop: `highlight.${this.config.useHighlightFrom}`
        });
      }
    },
    checkBoxActiveHelper(item) {
      if (!item.deleted)
        this.$emit("updateCheckboxState", { item, prop: "active" });
    },
    checkBoxCancelledHelper(item) {
      if (!item.deleted)
        this.$emit("updateCheckboxState", { item, prop: "cancelled" });
    },
    checkBoxMomentCancelledHelper(item) {
      if (!item.deleted)
        this.$emit("updateCheckboxStateMoment", { item, prop: "cancelled" });
    },
    checkIfDateIsInPast(item) {
      const date = new Date(item.moment.from);
      const now = new Date().setHours(0, 0, 0, 0);
      if (date < now) return true;
    },
    checkIfMomentIsCancelled(item) {
      return (
        item.moment?.cancelled ??
        (item.cancelled && !this.checkIfDateIsInPast(item))
      );
    },
    commonClasses(item) {
      return [
        item.deleted ? "deleted__event" : "",
        this.checkIfDateIsInPast(item) ? "in__past" : "event__title",
        item.active ? "" : "inactive__event",
        item.cancelled && !this.checkIfDateIsInPast(item)
          ? "cancelled__event"
          : "",
        this.checkIfMomentIsCancelled(item) ? "cancelled__event" : "",
        item.regionality.includes("internal") ? "intern__event" : ""
      ];
    },
    checkDuplicate(duplicate) {
      let apiKeyDuplicate = false;
      if (duplicate?.apiKeys?.length) {
        const eventConfig = this.$store?.state?.eventConfig || this.config;
        if (eventConfig) {
          const currentApiKeys = eventConfig?.apiKeys.map(ak => ak.apiKey);
          // any match between currentApiKeys and duplicate.apiKeys?
          apiKeyDuplicate = duplicate.apiKeys.some(ak =>
            currentApiKeys.includes(ak)
          );
        } else {
          // cant check for duplicates without eventConfig,
          // but it might be a duplicate. so return true
          console.info("No eventConfig found. Cant check for duplicates.");
          apiKeyDuplicate = true;
        }
      }
      if (apiKeyDuplicate) return true;
      if (duplicate?.principal) {
        // cant check for duplicates without principals,
        // but it might be a duplicate. so return true
        if (!this.$store.state.principals) {
          console.info("No principals found. Cant check for duplicates.");
          return true;
        }
        return this.$store.state.principals.find(
          p => p._id === duplicate?.principal
        );
      }
      return false;
    },
    stringifyDuplicate(duplicate) {
      if (duplicate.principal)
        return "Mandant: " + this.getPrincipalName(duplicate.principal);
      if (duplicate.apiKeys?.length) {
        return (
          "API-Keys: " +
            duplicate.apiKeys
              .map(ak => {
                return this.getApiKeyDescription(ak);
              })
              .join(", ") || ""
        );
      }
    }
  },
  computed: {
    selectedPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    currentHeaders() {
      if (!this.showPreviewImage)
        return this.headers.filter(i => i.value !== "image");
      return this.headers;
    },
    channels() {
      if (this.config?.channels?.join("") === "web") return []; // only web-channel => hide
      const activeStandardChannels = this.config?.standard_channels?.filter(c =>
        this.config?.channels?.includes(c.value)
      );
      let activeOwnChannels = this.config?.own_channels?.filter(c =>
        this.config?.channels?.includes(c.value)
      );
      return activeStandardChannels.concat(activeOwnChannels);
    },
    ownChannels() {
      return this.channels.filter(channel =>
        this.config.own_channels?.some(
          ownChannel => ownChannel.value === channel.value
        )
      );
    },
    tableOptions: {
      get() {
        // eslint-disable-next-line no-unused-vars
        const { s, ...filter } = this.filter;
        return filter;
      },
      set(value) {
        if (!this.filter.isDefault) delete value.isDefault;

        const diff = getObjectDiff(this.filter, value);
        if (!diff.length) return;

        value.limit = value.itemsPerPage;
        const sortDirection = value?.sortDesc?.[0] ? "desc" : "asc";

        value.sort = value?.sortBy?.[0] ?? "start";
        value.direction = sortDirection;
        this.$emit("applyFilter", {
          ...this.filter,
          ...value
        });
      }
    },
    activeImports() {
      return imports
        .filter(_ => _.isActive(this.selectedPrincipal))
        .map(_ => _.typeId);
    }
  }
};
